// Native & external imports
import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  Output,
  EventEmitter,
  inject,
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder } from "@angular/forms";
import { environment } from "src/environments/environment";
import {
  CmpAuthService,
  ApiCallsService,
  EnvSetupObject,
} from "icamppro-shared-components";
// Services
import { AppLoadService } from "src/services/app-load.service";
import { AppRouterService } from "src/routing/routes-service";
import { LocalStorageService } from "src/services/local-storage.service";
import { LayoutService } from "src/services/layout.service";
import { HelperService } from "src/services/helper.service";
// Objects
import { CmpRememberUserCamper } from "src/objects/cmp-family-member.object";
import { TokenDataObject } from "@iclassproinc/icamppro-shared-components";
import { SubSink } from "subsink";
import { Subject } from "rxjs";

@Component({
  standalone: false,
  selector: "cmp-camper-login",
  templateUrl: "./login.component.html",
})
export class LoginComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private apiService = inject(ApiCallsService);
  private routerService = inject(AppRouterService);
  private route = inject(ActivatedRoute);
  private localStorageService = inject(LocalStorageService);
  private formBuilder = inject(UntypedFormBuilder);
  private authService = inject(CmpAuthService);
  layoutService = inject(LayoutService);
  private helperService = inject(HelperService);
  private renderer = inject(Renderer2);
  private appLoadService = inject(AppLoadService);

  @Output() emitResponse = new EventEmitter();
  // PUBLIC
  public assetPrefix = environment.assetPrefix;
  public loginForm: UntypedFormGroup;
  public registrationForm: UntypedFormGroup;
  public authenticationStarted: boolean;
  public errorMessage: string;
  public classNames: { [index: string]: boolean };
  public showSlide: { [index: string]: { [index: string]: boolean } } = {};
  public showAlertModal = false;
  public showForgotPasswordModal = false;
  public validatingUser = false;
  public emailError = "";
  public loadingForgotPassword = false;
  public rememberCamper: CmpRememberUserCamper = new CmpRememberUserCamper();
  public shadowView = false;
  public textStringSettings: { [index: string]: string } = {};
  public suspended = false;
  public changePasswordToken: any;
  private cmpEnv: EnvSetupObject;
  public loginDetails: any;
  public consumerTypesList = { camper: "Go to Camp", staff: "Work at Camp" };
  private subSink = new SubSink();
  private _destroyed = false;
  public validatorSub$ = new Subject<{
    form: UntypedFormGroup;
    field?: string;
  }>();
  public createAccountValidatorSub$ = new Subject<string>();
  public controlsObj = {
    emailIsValid: true,
    formIsValid: false,
  };
  private emailPattern = this.helperService.emailPattern();
  public fromCreateAccount = false;

  public promptContent = "Welcome back!  What would you like to do today?";
  constructor() {
    this.cmpEnv = AppLoadService.getEnvSetupObject();
    this.layoutService.isAccountCreationLoader = false;
    // Password Reset
    this.changePasswordToken = this.route.snapshot.queryParamMap.get("token");
    this.layoutService.removeHasAlertClass(true); // resets the alert space
    if (this.changePasswordToken) {
      this.router.navigate([this.routerService.componentsUrl.changePassword], {
        queryParamsHandling: "merge",
      });
    }
    const fragment = this.route.snapshot.fragment;
    if (fragment && fragment.includes("shadowSessionEnd")) {
      this.shadowView = true;
    }

    this.errorMessage = "";
    this.authenticationStarted = false;
    this.classNames = {
      emailFocus: false,
      passwordFocus: false,
    };
  }

  ngOnInit() {
    Promise.resolve().then(() => {
      if (!this._destroyed) {
        this.layoutService.createAccountPage = true;
      }
    });
    this.initForms();
    this.initSubscription();
    this.initializeComponent();
    this.getTextStrings();
    this.addRemoveClass(this.registrationForm, "blur", "email");
    this.addRemoveClass(this.loginForm, "blur", "email");
    this.addRemoveClass(this.loginForm, "blur", "password");
    this.setRememberConsumerValues();
  }

  ngOnDestroy() {
    this._destroyed = true;
    this.layoutService.createAccountPage = false;
    this.subSink.unsubscribe();
  }

  private initForms() {
    this.loginForm = this.formBuilder.group({
      email: [null],
      password: [null],
    });

    this.registrationForm = this.formBuilder.group({
      email: [null],
      firstName: [null],
      lastName: [null],
    });
  }

  private initSubscription() {
    this.subSink.sink = this.validatorSub$.subscribe((data) => {
      const emailValue = data.form.value.email;
      this.controlsObj.formIsValid =
        Object.values(data.form.value).every((e) => !!e) &&
        this.emailPattern.test(emailValue);
      this.errorMessage = "";
      if (data.field === "email") {
        this.controlsObj.emailIsValid = true;
      }
    });
  }

  private resetControlsObject() {
    this.controlsObj.emailIsValid = true;
    this.controlsObj.formIsValid = false;
  }

  public validateEmail(form: UntypedFormGroup) {
    this.controlsObj.emailIsValid =
      !form.value.email || this.emailPattern.test(form.value.email);
  }

  private setRememberConsumerValues() {
    const userLocalStorage =
      this.localStorageService.getItem("remember_consumer");
    this.rememberCamper = userLocalStorage
      ? JSON.parse(userLocalStorage)
      : new CmpRememberUserCamper();
    if (this.rememberCamper.email !== "" && this.rememberCamper.email) {
      this.loginForm.controls["email"].setValue(this.rememberCamper.email);
      this.addRemoveClass(this.loginForm, "blur", "email");
    }
  }

  /**
   * @method initializeSlides
   * @description initialize slide variables
   */
  private initializeSlides() {
    this.showSlide = {
      // Slide 1 (Default slide)
      signIn: {
        showContent: false,
      },
      // Slide 2 - Select event
      checkEmailExist: {
        showContent: false,
      },
      // Slide - Add to bag
      createAccount: {
        showContent: false,
      },
      // Slide - View Shopping bag
      accountCreated: {
        showContent: false,
      },
      // Slide - View forgotPassword
      forgotPassword: {
        showContent: false,
      },
      // Slide - View resetPasswordEmailConfirm
      resetPasswordEmailConfirm: {
        showContent: false,
      },
      // Slide - Show Prompt Modal
      promptVerify: {
        showContent: false,
      },
    };
  }

  /**
   * @method showNext
   * @description show next slide (or) content to display
   * @returns void
   */
  public showNext(slideName = "") {
    this.initializeSlides();
    if (slideName === "signIn" || slideName === "checkEmailExist") {
      this.loginForm.reset();
      this.resetControlsObject();
      this.setRememberConsumerValues();
      this.registrationForm.reset();
      if (slideName === "checkEmailExist") {
        this.helperService.getCommunicationSetting();
        this.helperService.pushDataLayer(
          "create-account-start",
          "Create Account - Start",
        );
        if (!this.fromCreateAccount) {
          window.history.replaceState({}, "", "/create-account");
        }
      } else {
        this.helperService.pushDataLayer("login", "Login");
        this.addRemoveClass(this.loginForm, "blur", "email");
        this.addRemoveClass(this.loginForm, "blur", "password");
        if (
          window.location.pathname === "/create-account" &&
          typeof window.history.replaceState === "function"
        ) {
          window.history.replaceState(
            {},
            "",
            window.location.href.replace("/create-account", ""),
          );
        }
        this.fromCreateAccount = false;
      }
    } else if (slideName === "forgotPassword") {
      this.emailError = "";
      this.resetControlsObject();
      this.showForgotPasswordModal = true;
      if (typeof window.history.replaceState === "function") {
        window.history.replaceState(
          {},
          "",
          window.location.href.replace("/create-account", ""),
        );
      }
      this.showSlide["signIn"].showContent = true; // #rf8ahp - for showing the login slide at the back
      this.fromCreateAccount = false;
    } else if (slideName === "createAccount") {
      if (typeof window.history.replaceState === "function") {
        window.history.replaceState(
          {},
          "",
          window.location.href.replace("/create-account/", "/create-account"),
        );
      }
      this.addRemoveClass(this.loginForm, "blur", "email");
      this.addRemoveClass(this.loginForm, "blur", "password");
    }
    if (this.showSlide[slideName] && slideName !== "forgotPassword") {
      // #rf8ahp - for showing the login slide at the bac
      this.showSlide[slideName].showContent = true;
    }
  }

  /**
   * @method getTextStrings
   * @description get registration portal text string and set at heading and intro text
   * @returns void
   */
  public getTextStrings() {
    this.apiService
      .get(this.routerService.backendApiUrl.textStringSettings)
      .subscribe((response) => {
        if (!response.hasErrors) {
          if (response.data.loginHeading) {
            this.textStringSettings = response.data;
          } else {
            this.textStringSettings = response.data.defaultData;
          }
          this.textStringSettings.loginUserDetectedHeading =
            this.textStringSettings.loginUserDetectedHeading.replace(
              "{FirstName}",
              this.rememberCamper["firstName"],
            );
        }
      });
  }

  /**
   * @method initializeComponent
   * @description initialize modals variables
   */
  private async initializeComponent() {
    this.initializeSlides();
    const tenantInfo = await this.cmpEnv.getTenantInfo();
    if (!tenantInfo.isActive || tenantInfo.isSuspended) {
      this.suspended = true;
    }
    if (!this.suspended) {
      if (document.location) {
        let url = `${document.location.protocol}//${document.location.hostname}/${document.location.search}`;
        url = url.trim();
        if (url.match("utm_source")) {
          this.helperService.originalLocation = url;
          this.helperService.setUrl(document.location.search);
        } else {
          this.helperService.setUrl("");
        }
        if (
          document.location.pathname === "/create-account" ||
          document.location.pathname === "/create-account/"
        ) {
          window.history.replaceState(
            {},
            "",
            window.location.href.replace("/create-account/", "/create-account"),
          );
          this.fromCreateAccount = true;
          this.showNext("checkEmailExist");
        } else {
          this.fromCreateAccount = false;
          this.showNext("signIn");
        }
      }
    }
    if (this.changePasswordToken) {
      this.layoutService.containerClass = "container-small";
    } else {
      this.layoutService.containerClass = "container-large";
    }
  }

  /**
   * @method blurFormFields
   * @param formGroup: UntypedFormGroup
   * @description helps to blur form fields to validate formGroup properly
   */
  private blurFormFields(formGroup: UntypedFormGroup) {
    if (formGroup) {
      const keys = Object.keys(formGroup.controls);
      keys.forEach((key) => {
        const elem = document.getElementById(key);
        if (elem) {
          elem.blur();
        }
      });
    }
  }

  /**
   * @method onSubmitLoginDetails
   * @description To submit the login form details
   * @returns void
   */
  public onSubmitLoginDetails() {
    this.blurFormFields(this.loginForm);
    this.helperService.validateFormGroup(this.loginForm);
    if (!this.loginForm.valid) {
      this.errorMessage = "";
      return;
    }
    if (this.authenticationStarted) {
      return;
    }

    this.errorMessage = "";
    this.authenticationStarted = true;
    this.authService
      .login(
        this.loginForm.value.email,
        this.loginForm.value.password,
        "consumer",
      )
      .subscribe((response) => {
        if (!response.hasErrors && typeof response["data"] !== "undefined") {
          this.loginDetails = response.data;
          const data = response.data;
          const gender =
            this.loginDetails?.consumer_type === "staff"
              ? this.loginDetails.userConsumerProfile[this.loginDetails.id]
                  ?.gender
              : "";
          this.helperService.pushGTMEvent("login", true, gender);
          if (!response.data.routingPrompt) {
            this.loginRedirectUrl(response.data, "");
          } else {
            if (data.customizeAccountTypeLabels) {
              this.consumerTypesList.camper =
                data.registrationPortalLabel !== ""
                  ? data.registrationPortalLabel
                  : this.consumerTypesList.camper;
              this.consumerTypesList.staff =
                data.staffPortalLabel !== ""
                  ? data.staffPortalLabel
                  : this.consumerTypesList.staff;
            }
            this.showSlide["signIn"].showContent = false;
            this.showSlide["promptVerify"].showContent = true;
          }
        } else {
          this.authenticationStarted = false;
          this.errorMessage = response.message;
        }
      });
  }

  /**
   * @method storeUserData
   * @description Stores the logged in user data in localstorage
   */
  public storeUserData(data: TokenDataObject) {
    this.localStorageService.setItem("consumer", JSON.stringify(data));
    const isStaff = data.consumer_type === "staff" ? "true" : "false";
    localStorage.setItem("CMP_CONSUMER", data.access_token);
    localStorage.setItem("CMP_CONSUMER_PORTAL", "true");
    localStorage.setItem("staffUserLoggedIn", isStaff);
    // Flush local Storage data
    this.localStorageService.removeItems([
      "locations",
      "events",
      "eventSeries",
      "eventDate",
      "attendee",
      "subtotal",
      "tax",
      "total",
    ]);
  }

  /**
   * @method checkUserAlreadyExist
   * @description check user email whether it is already exist
   * @returns void
   */
  public checkUserAlreadyExist() {
    this.blurFormFields(this.registrationForm);
    this.helperService.validateFormGroup(this.registrationForm);
    if (this.registrationForm.valid) {
      this.validatingUser = true;
      const payLoad = this.registrationForm.value;
      payLoad.scope = "consumer";
      this.apiService
        .post(this.routerService.backendApiUrl.emailValidation, payLoad)
        .subscribe((response) => {
          if (!response.hasErrors) {
            this.showNext("createAccount");
          } else {
            const emailError = response.errors.find(
              (error: { field: string; message: string }) =>
                error.field === "email",
            );
            if (emailError) {
              if (emailError.message.indexOf("registered") !== -1) {
                this.showAlertModal = true;
              } else {
                this.emailError = emailError.message;
                this.registrationForm.controls["email"].setErrors({
                  incorrect: true,
                });
              }
            }
          }
          this.validatingUser = false;
        });
    }
  }

  /**
   * @method addRemoveClass
   * @description add or remove the input-focused class
   * @param formGroup UntypedFormGroup
   * @param action string
   * @param element string
   * @returns response callback
   */
  public addRemoveClass(
    formGroup: UntypedFormGroup,
    action = "",
    element = "",
    event?: any,
  ) {
    const clickedIcon =
      event &&
      event.relatedTarget &&
      event.relatedTarget.classList.contains("view-toggle-2-icons");
    switch (element) {
      case "email":
        this.classNames.emailFocus =
          action === "focus"
            ? true
            : formGroup.controls["email"].value
              ? true
              : false;
        if (action === "focus") {
          this.classNames.passwordFocus = formGroup.controls["password"].value
            ? true
            : false;
        }
        break;
      case "password":
        if (clickedIcon) {
          this.classNames.passwordFocus = true;
        } else {
          this.classNames.passwordFocus =
            action === "focus"
              ? true
              : formGroup.controls["password"].value
                ? true
                : false;
        }
        break;
      case "registerEmail":
        this.classNames.registerEmailFocus =
          action === "focus"
            ? true
            : formGroup.controls["email"].value
              ? true
              : false;
        break;
      case "firstName":
        this.classNames.firstNameFocus =
          action === "focus"
            ? true
            : formGroup.controls["firstName"].value
              ? true
              : false;
        break;
      case "lastName":
        this.classNames.lastNameFocus =
          action === "focus"
            ? true
            : formGroup.controls["lastName"].value
              ? true
              : false;
        break;
    }
  }

  /**
   * @method closePopup
   * @description On clicking of cancel or close button of modal popup
   */
  public triggerAction(modalResponse: {
    from: string;
    to: string;
    value: any;
  }) {
    switch (modalResponse.from) {
      case "camperRegistration":
        if (modalResponse.to === "openAlertModal") {
          this.showAlertModal = true;
        } else if (modalResponse.to === "accountCreated") {
          this.helperService.resetGtm();
          if (this.appLoadService.getAppSettings) {
            this.helperService.pushGTMEvent("sign_up", true);
          }
          window.location.href = modalResponse.value.domain;
        } else {
          this.addRemoveClass(this.loginForm, "blur", "email");
          this.addRemoveClass(this.loginForm, "blur", "password");
          this.showNext(modalResponse.to);
        }
        break;
      case "passwordReset":
        this.showNext("signIn");
        this.showForgotPasswordModal = false;
        break;
    }
  }

  /**
   * @method closeAlert
   * @param action string
   * @description closes the alert modal
   */
  // #2d8w89 - No validation on primary email when creating household account in Reg Portal
  public closeAlert(action = "") {
    this.addRemoveClass(this.loginForm, "blur", "email");
    this.showAlertModal = false;
    this.showNext(action);
  }

  /**
   * @method newUser
   * @description On selecting a New account.
   */
  public newUser() {
    this.localStorageService.removeItem("remember_consumer");
    this.rememberCamper = new CmpRememberUserCamper();
    this.loginForm.controls["email"].setValue("");
    this.addRemoveClass(this.loginForm, "blur", "email");
  }

  /**
   * @method loginRedirectUrl
   * @description login redirect to particular portal
   * @param response object
   * @param users string
   * @returns response callback
   */
  loginRedirectUrl(response: any, users: string) {
    if (users !== "") {
      if (users == "camper") {
        window.location.href = response.registrationPortalUrl;
        this.storeUserData(response);
      } else {
        window.location.href = response.staffPortalUrl;
        this.storeUserData(response);
      }
    } else {
      if (response.consumer_type === "camper") {
        window.location.href = response.domain;
        this.storeUserData(response);
      } else {
        this.storeUserData(response);
        window.location.href = response.domain;
      }
    }
  }

  /**
   * @method viewPassword
   * @description Hide/View password icon
   * @returns void
   */
  public viewPassword(field: string) {
    if (field === "password") {
      if (document.activeElement.id === "viewSignInPassword") {
        this.renderer.selectRootElement("#password").focus();
      }
    }
  }

  public forgotPassword() {
    // Show Forgot password comp
    this.emitResponse.emit({ from: "loginModal", value: "passwordReset" });
  }
}
